import React, { useRef } from 'react';

interface StreamingGuardProps {
  /** The content to display — may be undefined/empty during partial streaming. */
  children: React.ReactNode;
  /**
   * When true, children are considered potentially incomplete.
   * If children would render as "empty" (null/undefined/fragment with no visible content),
   * the guard holds the last valid snapshot instead of going blank.
   */
  isStreaming: boolean;
  /**
   * A function that checks whether the current children are "valid" — i.e. have
   * enough content to be worth showing. Defaults to checking children != null.
   * Return false to hold the previous snapshot; return true to promote.
   */
  isValid?: (children: React.ReactNode) => boolean;
  /**
   * Placeholder to show when there's no valid snapshot yet (first render, before
   * any content has arrived). If omitted, renders nothing.
   */
  fallback?: React.ReactNode;
}

/**
 * StreamingGuard — anti-flicker wrapper for streaming tool content.
 *
 * During streaming, tool call args arrive as partial JSON that often fails to
 * parse or produces incomplete props. This causes child components (iframes,
 * dashboards, etc.) to briefly render nothing, then snap back — visible as a
 * flicker or full unmount/remount.
 *
 * StreamingGuard solves this by caching the last valid render. If the current
 * children would be empty/invalid, it keeps showing the previous snapshot
 * until new valid content arrives. The component never goes blank.
 *
 * Usage:
 *   <StreamingGuard isStreaming={isStreaming} fallback={<Skeleton />}>
 *     <DashboardRenderer config={parsedConfig} />
 *   </StreamingGuard>
 */
export const StreamingGuard: React.FC<StreamingGuardProps> = ({
  children,
  isStreaming,
  isValid,
  fallback,
}) => {
  const lastValidRef = useRef<React.ReactNode>(null);

  const valid = isValid ? isValid(children) : children != null;

  if (valid) {
    lastValidRef.current = children;
  }

  // During streaming: show last valid if current is invalid
  if (isStreaming && !valid) {
    if (lastValidRef.current != null) {
      return <>{lastValidRef.current}</>;
    }
    return fallback ? <>{fallback}</> : null;
  }

  // Not streaming: show current children directly (even if empty — streaming is done)
  if (!isStreaming) {
    return <>{children}</>;
  }

  // Streaming and valid: show current
  return <>{children}</>;
};
